import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Modal, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'

export interface GroupEvaluationStepNode {
	id: string
	label: string
	currentGroupId: string | null
}

interface RaterGroupModalProps {
	isOpen: boolean
	onClose: () => void
	organizationId: string
	cycleId: string
	editing?: {
		id: string
		name: string
		autoPopulateFromParticipants: boolean
		required: boolean
	} | null
	stepNodes: GroupEvaluationStepNode[]
	initialStepNodeId?: string | null
	onSaved?: () => void
}

const EMPTY_POPULATION = {
	roles: [] as string[],
	hierarchyLevels: [] as number[],
	whitelistedUsers: [] as string[],
	blacklistedUsers: [] as string[],
	blacklistedHierarchyRoles: [] as string[],
}

export function RaterGroupModal({
	isOpen,
	onClose,
	organizationId,
	cycleId,
	editing,
	stepNodes,
	initialStepNodeId,
	onSaved,
}: RaterGroupModalProps) {
	const { showToast } = useToast()
	const queryClient = useQueryClient()

	const [name, setName] = useState('')
	const [autoPopulate, setAutoPopulate] = useState(true)
	const [required, setRequired] = useState(true)
	const [stepNodeId, setStepNodeId] = useState<string>('')

	useEffect(() => {
		if (isOpen) {
			setName(editing?.name ?? '')
			setAutoPopulate(editing?.autoPopulateFromParticipants ?? true)
			setRequired(editing?.required ?? true)
			setStepNodeId(initialStepNodeId ?? (stepNodes.length === 1 ? stepNodes[0].id : ''))
		}
	}, [isOpen, editing, initialStepNodeId, stepNodes])

	const invalidate = () => {
		queryClient.invalidateQueries(
			trpc.admin.cycles.reviewerGroups.search.queryFilter({ cycleId, page: 1, perPage: 50 }),
		)
		queryClient.invalidateQueries(trpc.admin.cycles.steps.diagram.show.queryFilter({ cycleId }))
	}

	const attachMutation = useMutation({
		...trpc.admin.dealTeam.attachReviewerGroupToStep.mutationOptions(),
	})

	const detachMutation = useMutation({
		...trpc.admin.dealTeam.detachReviewerGroup.mutationOptions(),
	})

	const finishWithSuccess = (message: string) => {
		showToast(message)
		invalidate()
		onSaved?.()
		onClose()
	}

	const finishWithAttachError = (message: string) => {
		// The rater group itself was saved — only the step linkage failed. Close
		// the modal and refresh the list so the new group is visible, but show a
		// single combined error so the failure isn't hidden behind a success toast.
		showToast(message, 'error')
		invalidate()
		onSaved?.()
		onClose()
	}

	const createMutation = useMutation({
		...trpc.admin.dealTeam.createReviewerGroup.mutationOptions(),
		onSuccess: async (group) => {
			if (stepNodeId) {
				try {
					await attachMutation.mutateAsync({
						organizationId,
						cycleId,
						stepNodeId,
						reviewerGroupId: group.id,
					})
				} catch (err) {
					finishWithAttachError(`Rater group created but linking to step failed: ${(err as Error).message}`)
					return
				}
			}
			finishWithSuccess('Rater group created')
		},
		onError: (err) => showToast(`Failed to create: ${err.message}`, 'error'),
	})

	const editMutation = useMutation({
		...trpc.admin.dealTeam.editReviewerGroup.mutationOptions(),
		onSuccess: async (group) => {
			if (stepNodeId) {
				try {
					await attachMutation.mutateAsync({
						organizationId,
						cycleId,
						stepNodeId,
						reviewerGroupId: group.id,
					})
				} catch (err) {
					finishWithAttachError(`Rater group saved but linking to step failed: ${(err as Error).message}`)
					return
				}
			} else if (initialStepNodeId) {
				// User had a step linked and explicitly cleared the dropdown. Detach
				// the existing linkage so the diagram reflects the user's choice.
				try {
					await detachMutation.mutateAsync({
						organizationId,
						cycleId,
						reviewerGroupId: group.id,
					})
				} catch (err) {
					finishWithAttachError(`Rater group saved but detaching from step failed: ${(err as Error).message}`)
					return
				}
			}
			finishWithSuccess('Rater group saved')
		},
		onError: (err) => showToast(`Failed to save: ${err.message}`, 'error'),
	})

	const handleSave = () => {
		if (!name.trim()) {
			showToast('Name is required', 'error')
			return
		}

		const payload = {
			organizationId,
			cycleId,
			name: name.trim(),
			population: EMPTY_POPULATION,
			minReviewers: 0,
			maxReviewers: 0,
			required,
			prefill: false,
			optional: false,
			shownForRoles: [] as string[],
			requireDeclineReason: false,
			autoPopulateFromParticipants: autoPopulate,
		}

		if (editing) {
			editMutation.mutate({ ...payload, id: editing.id })
		} else {
			createMutation.mutate(payload)
		}
	}

	const isSaving =
		createMutation.isPending || editMutation.isPending || attachMutation.isPending || detachMutation.isPending

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={editing ? 'Edit rater group' : 'Add rater group'}
			className='!w-[560px]'>
			<div className='flex flex-col gap-4'>
				<div className='flex flex-col gap-1'>
					<Text variant='small' weight='semibold'>
						Name
					</Text>
					<input
						type='text'
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder='e.g. Deal team peer review'
						className='border border-neutral-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'
					/>
				</div>

				<label className='flex items-start gap-3 p-3 rounded border border-neutral-200 bg-neutral-50 cursor-pointer'>
					<input
						type='checkbox'
						checked={autoPopulate}
						onChange={(e) => setAutoPopulate(e.target.checked)}
						className='mt-0.5'
					/>
					<div className='flex flex-col gap-1'>
						<Text variant='small' weight='semibold'>
							Auto-populate from deal team participants (360°)
						</Text>
						<Text variant='xsmall' className='text-neutral-600'>
							When the trigger fires, every member of the deal team will rate every other member in this group. Min and
							max raters are set to (team size − 1) automatically. Self-review is skipped.
						</Text>
					</div>
				</label>

				<div className='flex flex-col gap-1'>
					<Text variant='small' weight='semibold'>
						Attach to group evaluation step
					</Text>
					{stepNodes.length === 0 ? (
						<div className='p-3 rounded border border-amber-200 bg-amber-50'>
							<Text variant='xsmall' className='text-amber-700'>
								This template has no group evaluation steps. Add one in the cycle's steps diagram first, then come back
								to attach this rater group.
							</Text>
						</div>
					) : (
						<>
							<select
								value={stepNodeId}
								onChange={(e) => setStepNodeId(e.target.value)}
								className='border border-neutral-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'>
								<option value=''>(Don't attach — link manually later)</option>
								{stepNodes.map((node) => {
									const linkedToOther = node.currentGroupId && node.currentGroupId !== editing?.id
									return (
										<option key={node.id} value={node.id}>
											{node.label}
											{linkedToOther ? ' — already linked to another group' : ''}
										</option>
									)
								})}
							</select>
							<Text variant='xsmall' className='text-neutral-500'>
								The selected step will use this rater group. Any existing attachment on that step is replaced.
							</Text>
						</>
					)}
				</div>

				<label className='flex items-center gap-2 cursor-pointer'>
					<input type='checkbox' checked={required} onChange={(e) => setRequired(e.target.checked)} />
					<Text variant='small'>Required</Text>
				</label>

				<div className='flex justify-end gap-2 pt-2 border-t border-neutral-200'>
					<Button variant='secondary' onClick={onClose} disabled={isSaving}>
						Cancel
					</Button>
					<Button onClick={handleSave} disabled={isSaving}>
						{isSaving ? 'Saving…' : editing ? 'Save' : 'Create'}
					</Button>
				</div>
			</div>
		</Modal>
	)
}
